import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import viewIcon from "../assets/Images/View.png";
import editIcon from "../assets/Images/Edit.png";
import deleteIcon from "../assets/icons/deleteIcon.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import addIcon from "../assets/Images/AddIcon.png";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import crossIcon from "../assets/icons/crossIcon.png";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import usersIcon from "../assets/icons/users.png";
import conversion from "../assets/icons/conversation.png";
import modelsIcon from "../assets/icons/modelsvg.png";
import { useDispatch } from "react-redux";
import { setSelectedMenu } from "../redux/sidebarSlice";

const BASE_URL = `${process.env.REACT_APP_API_URL}/users`;
const ENTERPRISE_URL = `${process.env.REACT_APP_API_URL}/enterprises`;
const ROLE_URL = `${process.env.REACT_APP_API_URL}/roles`;
const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const IP_MANAGEMENT = `${process.env.REACT_APP_API_URL}/allowed-ips`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [usersIPs, setUsersIPs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [viewMode, setViewMode] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [userRoleName, setUserRoleName] = useState("");
  const [ipCounts, setIPCounts] = useState({
    total: 0,
    active: 0,
    blocked: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchRolesAndUser = async () => {
      try {
        const res = await api.get(`${process.env.REACT_APP_API_URL}/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const roles = res.data;

        setRoleList(roles);

        const currentUser = JSON.parse(
          localStorage.getItem("user") || sessionStorage.getItem("user")
        );

        const userRole = roles.find((r) => r.role_id === currentUser?.role_fid);
        if (userRole) setUserRoleName(userRole.role_name);
      } catch (err) {
        console.error("Error fetching roles or user info", err);
      }
    };

    fetchRolesAndUser();
  }, []);

  const currentUser = JSON.parse(
    localStorage.getItem("user") || sessionStorage.getItem("user")
  );

  // For when user comes via navigation (e.g., from enterprise click)
  const preselectedEnterpriseId = location.state?.enterpriseId || null;

  // Use this only when needed separately
  const currentUserEnterpriseId = currentUser?.enterprise_fid || null;

  useEffect(() => {
    if (preselectedEnterpriseId) {
      setFormData((prev) => ({
        ...prev,
        enterprise_id: preselectedEnterpriseId,
      }));
    }
  }, [preselectedEnterpriseId]);

  const [formData, setFormData] = useState({
    name: "",
    enterprise_id: "",
    ip_address: "",
    api_user_count: "",
    status: "",
  });

  const [previewUrl, setPreviewUrl] = useState("");

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchData = async () => {
    try {
      const [roleRes, enterpriseRes] = await Promise.all([
        api.get(ROLE_URL, { headers: { Authorization: `Bearer ${token}` } }),
        api.get(ENTERPRISE_URL, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setRoles(roleRes.data);
      setEnterprises(enterpriseRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const fetchd = async () => {
    try {
      const res = await api.get(BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: "all" },
      });

      // Fetch IPs using POST method for the IP management endpoint
      const res1 = await fetch(`${IP_MANAGEMENT}/getip`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          enterprise_fid:
            currentUser?.enterprise_fid || preselectedEnterpriseId,
        }),
      });

      const data = await res1.json();
      setUsersIPs(data); // Set the fetched IPs to state

      // setUsers(res.data.data); // Set users data to state
    } catch (error) {
      console.error("Failed to fetch users", error);
      showErrorToast(TOAST_MESSAGES.USER.FETCH_ERROR);
    }
  };

  const fetchStats = async () => {
    try {
      const [ipCountsRes] = await Promise.all([
        fetch(`${IP_MANAGEMENT}/counts`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            enterprise_fid:
              currentUser?.enterprise_fid || preselectedEnterpriseId,
          }),
        }),
      ]);

      const ipData = await ipCountsRes.json();
      if (ipData.success && ipData.data) {
        setIPCounts({
          total: ipData.data.total,
          active: ipData.data.active,
          blocked: ipData.data.blocked,
        });
      }
    } catch (err) {
      console.error("fetchStats error:", err);
      showErrorToast(TOAST_MESSAGES.ENTERPRISE.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchData();
    fetchd();
  }, []);

  const filteredUsers = usersIPs
    .filter((user) => {
      // Filter by preselectedEnterpriseId if provided
      if (preselectedEnterpriseId) {
        return user.enterprise_fid === preselectedEnterpriseId;
      }
      return true; // Return all if no preselectedEnterpriseId
    })
    .filter(
      (user) =>
        // Filter by user name or user email (correct the field names to 'name' and 'ip_address')
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.ip_address?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter((user) => {
      // Filter by date (Today or Yesterday)
      if (!dayFilter) return true; // No filter if dayFilter is empty
      const createdDate = new Date(user.created_timestamp);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set today's time to midnight
      if (dayFilter === "Today") return createdDate >= today; // Filter for today's IPs
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1); // Get yesterday's date
        return createdDate >= yesterday && createdDate < today; // Filter for yesterday's IPs
      }
      return true; // Return all if no specific day filter
    })
    .sort((a, b) => {
      // Sort by updated_timestamp, ascending or descending
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  useEffect(() => {
    if (showForm && userRoleName === "Admin" && currentUserEnterpriseId) {
      setFormData((prev) => ({
        ...prev,
        enterprise_id: currentUserEnterpriseId,
      }));
    }
  }, [showForm, userRoleName, currentUserEnterpriseId]);

  const totalEntries = filteredUsers.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredUsers.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      enterprise_id: "",
      ip_address: "",
      api_user_count: "",
      status: "",
    });
    setPreviewUrl("");
    setEditingId(null);
    setShowForm(false);
    setViewMode(false);
  };

  const handleSubmit1 = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); // Start loading

    try {
      // Prepare JSON payload instead of FormData
      const payload = {
        name: formData.name || "",
        ip_address: formData.ip_address || "",
        enterprise_fid: formData.enterprise_id || "",
      };

      if (editingId) {
        await api.put(`${IP_MANAGEMENT}/${editingId}`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        showSuccessToast(TOAST_MESSAGES.USER.UPDATE_SUCCESS);
      } else {
        await api.post(`${IP_MANAGEMENT}/add`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        showSuccessToast(TOAST_MESSAGES.USER.CREATE_SUCCESS);
      }

      resetForm();
      fetchData();
      fetchd();
    } catch (err) {
      console.error("Submit error:", err);
      showErrorToast(
        editingId
          ? TOAST_MESSAGES.USER.UPDATE_ERROR
          : TOAST_MESSAGES.USER.CREATE_ERROR
      );
    } finally {
      setIsSubmitting(false); // Stop loading
    }
  };

  const handleEdit = (user) => {
    setFormData({
      name: user.name, // correct field
      ip_address: user.ip_address,
      enterprise_id: String(user.enterprise_fid),
      api_user_count: user.api_user_count,
      status: user.status,
    });

    setEditingId(user.user_allowed_ip_id); // <-- use correct ID
    setViewMode(false);
    setShowForm(true);
    fetchStats();
  };

  const handleView = (user) => {
    setFormData({
      name: user.name,
      ip_address: user.ip_address,
      enterprise_id: String(user.enterprise_fid),
      api_user_count: user.api_user_count,
      status: user.user_status,
    });

    setViewMode(true);
    setShowForm(true);
  };

  const handleStatusChange = async (user) => {
    const newStatus = user.status === "active" ? "blocked" : "active";
    const statusText = newStatus === "active" ? "Active" : "Inactive";

    try {
      await api.patch(
        `${IP_MANAGEMENT}/${user.user_allowed_ip_id}/status`, // <-- correct ID
        {
          status: newStatus,
          updated_by: user.updated_by, // or current user id
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      fetchd(); // Refresh the data
      fetchStats();
      showSuccessToast(`User status changed to ${statusText}`);
    } catch (err) {
      console.error("Status change error:", err);
      showErrorToast(TOAST_MESSAGES.USER.STATUS_CHANGE_ERROR);
    }
  };

  const exportExcel = () => {
    const wsData = [
      ["Name", "IP Address", "Enterprise", "Status", "Last Updated"],

      ...filteredUsers.map((u) => {
        // Find enterprise name using enterprise_fid
        const enterpriseName =
          enterprises.find(
            (ent) => Number(ent.enterprise_id) === Number(u.enterprise_fid)
          )?.enterprise_name || "";

        // Format last updated timestamp
        const lastUpdated = u.updated_timestamp
          ? new Date(u.updated_timestamp).toLocaleString()
          : "";

        return [
          u.name || "",
          u.ip_address || "",
          enterpriseName,
          u.status === "active" ? "Active" : "Inactive",
          lastUpdated,
        ];
      }),
    ];

    // Create workbook & worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "User IPs");

    // Write file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "users_ips.xlsx");
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const statsData = [
    // {
    //   label: "Total Users",
    //   value: regularUsersCount.total + adminUsersCount.total || 0,
    //   summary: [
    //     {
    //       label: "Active Users",
    //       value: regularUsersCount.active + adminUsersCount.active,
    //     },
    //     {
    //       label: "Inactive Users",
    //       value: regularUsersCount.inactive + adminUsersCount.inactive,
    //     },
    //   ],
    //   icon: usersIcon,
    // },
    {
      label: "IPs",
      value: ipCounts.total,
      summary: [
        { label: "Active", value: ipCounts.active },
        { label: "Blocked", value: ipCounts.blocked },
      ],
      icon: usersIcon,
    },
  ];

  const getVisiblePages = () => {
    let start = currentPage - 1;
    let end = currentPage + 1;

    // Fix lower bound
    if (start < 1) {
      start = 1;
      end = 2;
    }

    // Fix upper bound
    if (end > totalPages) {
      end = totalPages;
      start = totalPages - 1;
      if (start < 1) start = 1;
    }

    let pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div className="mx-2 pt-1 h-100">
      <div
        className="d-flex flex-wrap gap-3 mb-2 mt-0  justify-content-evenly align-items-center"
        style={{ maxWidth: "400px" }}
      >
        {statsData.map((item, index) => (
          <div key={index} className="dashboard-box p-3 rounded shadow-sm">
            <div className="d-flex align-items-center mb-1">
              {item.icon && (
                <img
                  src={item.icon}
                  alt={item.label}
                  style={{
                    width: "21px",
                    height: "21px",
                  }}
                  className="icon-image1"
                />
              )}
              <h6 className="ms-2 mb-0">{item.label}</h6>
            </div>

            <h2 className="fw-bold">
              {typeof item.value !== "undefined" ? item.value : "0"}
            </h2>

            <div className="box-summary">
              {item.summary && item.summary.length > 0 ? (
                item.summary.map((s, i) => (
                  <span key={i}>
                    {s.label}: {s.value}
                  </span>
                ))
              ) : (
                <span className="invisible">placeholder</span> // Keeps space but invisible
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold refresh-prompts" style={{ fontSize: "20px" }}>
          IP Management Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Users"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button>

          <div className="">
            <button
              className="btn icon-button filter-dropdown-toggle action-button"
              type="button"
              data-bs-toggle="dropdown"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts">Time</h6>
              <hr />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label className="form-check-label" htmlFor={order}>
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr />
              <h6 className="fw-bolder refresh-promts">By Day</h6>
              <hr />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label className="form-check-label" htmlFor={day || "all"}>
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr />
              <div className="d-flex justify-content-between mt-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={() => {
                    setCurrentPage(1);
                  }}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button text-dark ms-2"
            onClick={() => {
              setEditingId(null);
              setViewMode(false);
              setFormData((prev) => ({
                ...prev,
                enterprise_id: preselectedEnterpriseId || "",
              }));
              setShowForm(true);
            }}
          >
            <img src={addIcon} className="icon-image" alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts fs-5">No entries found</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table user-table1 table-sm w-100 border">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Ip Address</th>
                  <th>Enterprise</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((user, i) => (
                  <tr key={user.user_allowed_ip_id || i}>
                    <td>
                      {user.name
                        ? user.name.charAt(0).toUpperCase() + user.name.slice(1)
                        : "—"}
                    </td>
                    <td>{user.ip_address}</td>
                    <td>
                      {enterprises.find(
                        (ent) =>
                          Number(ent.enterprise_id) ==
                          Number(user.enterprise_fid)
                      )?.enterprise_name || (
                        <span className="refresh-prompts">Unknown</span>
                      )}
                    </td>

                    <td>
                      {user.status?.toLowerCase() === "active" ? (
                        <span className="text-success">Active</span>
                      ) : (
                        <span className="text-danger">Inactive</span>
                      )}
                    </td>

                    <td>{formatDateToUserTimeZone(user.updated_timestamp)}</td>

                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(user)}
                        title="View"
                      >
                        <img src={viewIcon} className="icon-image" alt="View" />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleEdit(user)}
                        title="Edit"
                      >
                        <img src={editIcon} className="icon-image" alt="Edit" />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleStatusChange(user)}
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer text-center bgcolor fixed-bottom-footer d-md-flex justify-content-sm-between align-items-center ">
            <div className="refresh-prompts pb-2 pb-md-0">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0 justify-content-center">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {getVisiblePages().map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-75 mx-auto">
              <form onSubmit={handleSubmit1}>
                <div className="modal-header justify-content-center">
                  <h5 className="modal-title">
                    {viewMode
                      ? "View IP details"
                      : editingId
                      ? "Update IP details"
                      : "Add IP details"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={resetForm}
                    aria-label="Close"
                  >
                    <img src={crossIcon} className="icon-image" alt="Close" />
                  </button>
                </div>

                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Enterprise</label>
                      <select
                        value={formData.enterprise_id || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            enterprise_id: e.target.value,
                          })
                        }
                        className="form-select"
                        disabled={userRoleName !== "Superadmin" || viewMode}
                        required
                      >
                        <option value="">Select Enterprise</option>
                        {enterprises.map((enterprise) => (
                          <option
                            key={enterprise.enterprise_id}
                            value={enterprise.enterprise_id}
                          >
                            {enterprise.enterprise_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">IP Address</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.ip_address || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            ip_address: e.target.value,
                          })
                        }
                        autoComplete="off"
                        required
                        disabled={viewMode}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="cancle-btn"
                    onClick={resetForm}
                  >
                    {viewMode ? "Close" : "Cancel"}
                  </button>
                  {!viewMode && (
                    <button
                      type="submit"
                      className="custom-btn"
                      disabled={isSubmitting}
                    >
                      {editingId ? "Update" : "Submit"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
